import {useState, useCallback} from 'react'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {cardIcon, squareBigLogo, infoIcon} from '@img/index.js'
import {usePaymentMethods, useInvalidatePaymentMethods, useSetDefaultPaymentMethod, useDeletePaymentMethod} from '@/hooks/usePaymentMethods'
import AddCardPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PaymentMethodsPopup.jsx'
import PayAfterDropoffPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PayAfterDropoffPopup.jsx'
import CardBrand from '@/customComponents/uiComponents/global/CardBrand.jsx'
import Tooltip from '@/customComponents/uiComponents/global/Tooltip.jsx'
import {parseCard} from '@/utils/functions'

export default function PaymentMethodsList() {
    const {data: cards = [], isLoading} = usePaymentMethods()
    const invalidate = useInvalidatePaymentMethods()
    const setDefault = useSetDefaultPaymentMethod()
    const deleteCard = useDeletePaymentMethod()
    const [showAddCard, setShowAddCard] = useState(false)
    const [showPayAfterInfo, setShowPayAfterInfo] = useState(false)

    const [deleteConfirmId, setDeleteConfirmId] = useState(null)
    const deleteError = deleteCard.error?.message

    const handleDelete = (id) => {
        if (deleteConfirmId === id) {
            deleteCard.reset()
            deleteCard.mutate(id)
            setDeleteConfirmId(null)
        } else {
            deleteCard.reset()
            setDeleteConfirmId(id)
        }
    }

    const handleCardAdded = useCallback(() => {
        invalidate()
        setShowAddCard(false)
    }, [invalidate])

    return (
        <div className="white-global-section">
            <div className="flex items-center gap-2 mb-6">
                <img src={cardIcon} alt="card"/>
                <h5 className="mb-0! font-bold">Payment Methods</h5>
            </div>

            {isLoading ? (
                <div className="flex flex-col gap-5 mb-5 animate-pulse">
                    {[1, 2].map(i => (
                        <div key={i} className="flex items-center gap-10 bg-(--bg-grey) rounded-2xl px-4 py-3 h-16">
                            <div className="w-12 h-8 bg-gray-200 rounded"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : cards.length === 0 ? (
                <p className="text-(--text-secondary) mb-5!">No payment methods saved yet</p>
            ) : (
                <div className="flex flex-col gap-5 mb-5">
                    {cards.map((card) => {
                        const {brand, last4, expiry} = parseCard(card)
                        const rawLabel = card.display_name || card.payment_method_type_label || 'Card'
                        const nameMatch = rawLabel.match(/^[A-Za-z][A-Za-z\s.'-]*[A-Za-z]/)
                        const label = last4 ? (nameMatch?.[0].trim() || 'Card') : rawLabel
                        return (
                            <div key={card.id} className="flex items-center gap-10 bg-(--bg-grey) rounded-2xl px-4 py-5 max-md:flex-col max-md:items-stretch max-md:gap-3">
                                <div className="flex items-center gap-4 max-md:gap-2 flex-1 max-md:w-full">
                                    <CardBrand brand={brand} className="w-10 shrink-0"/>
                                    <div className="flex-1">
                                        <span className="font-medium block">
                                            {label}
                                            {last4 && (
                                                <span className="font-mono tracking-wider ml-2 align-middle">************{last4}</span>
                                            )}
                                        </span>
                                        {expiry && <span className="text-(--text-secondary) text-sm">Valid Thru: {expiry}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 max-md:w-full max-md:justify-start">
                                    <span className="relative inline-flex">
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(card.id)}
                                            onBlur={() => setDeleteConfirmId(null)}
                                            disabled={deleteCard.isPending && deleteCard.variables === card.id}
                                            aria-label="Delete card"
                                            className="cursor-pointer rounded-full disabled:opacity-50 disabled:cursor-default bg-(--text-red) text-white text-sm px-3 py-1">
                                            {deleteCard.isPending && deleteCard.variables === card.id ? <Loader color="#fff"/> : 'Delete'}
                                        </button>
                                        {deleteConfirmId === card.id && <Tooltip text="Click again to confirm" position="top"/>}
                                    </span>
                                    {card.is_default ? (
                                        <span className="bg-(--bg-dark) text-white text-xs font-medium px-3 py-1 rounded-full min-w-20 h-7 inline-flex items-center justify-center">Default</span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setDefault.mutate(card.id)}
                                            disabled={setDefault.isPending && setDefault.variables === card.id}
                                            className="cursor-pointer bg-(--bg-dark-grey) text-(--text-primary) text-sm font-medium px-3 py-1 rounded-full min-w-20 h-7 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-default">
                                            {setDefault.isPending && setDefault.variables === card.id ? <Loader color="#000"/> : 'Default'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {deleteError && (
                <p className="text-(--text-red) mb-5!">{deleteError}</p>
            )}

            <button
                type="button"
                onClick={() => setShowAddCard(true)}
                style={{backgroundColor:'#000'}}
                className="cursor-pointer flex items-center gap-2 bg-[url('/add-circle-icon.svg')] bg-no-repeat bg-position-[right_8px_center] text-white text-lg px-30 py-4 rounded-full mb-5 max-md:w-full max-md:px-10 max-md:justify-center"
            >
                Add Card
            </button>

            <div className="flex items-center gap-4 mt-10 rounded-xl p-4 bg-(--bg-grey)">
                <span>
                    <img alt="square" src={squareBigLogo}/>
                </span>
                <div className="flex-1 text-sm relative pr-6">
                    <button type="button" onClick={() => setShowPayAfterInfo(true)} className="cursor-pointer absolute top-0 right-0"><img alt="info" src={infoIcon}/></button>
                    <h5 className="mb-0! font-bold">Pay after drop off</h5>
                    <p className="mb-0!">Square holds funds. Charged after drop-off</p>
                </div>
            </div>

            <AddCardPopup isOpen={showAddCard} onClose={() => setShowAddCard(false)} onCardAdded={handleCardAdded} showSetDefault={true}/>
            <PayAfterDropoffPopup isOpen={showPayAfterInfo} onClose={() => setShowPayAfterInfo(false)}/>
        </div>
    )
}
